import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Control, useForm, useWatch } from 'react-hook-form';
import { FormEventHandler, PropsWithChildren, createContext, useContext, useEffect, useMemo, useState } from 'react';

import type { ApiError } from '../../../../../../@types';
import { QUERY_KEYS } from '../../../../../../constants';
import API_ROUTES from '../../../../../../constants/apiRoutes';
import { useToast } from '../../../../../../contexts/ToastContext';
import { useToastErrorMessage } from '../../../../../../hooks/useToastErrorMessage';
import { useMarketExclusionSports } from '../../../../../../hooks/useMarketExclusionSports';
import {
  BonusMarketTypeExclusionSelectorFormData,
  bonusMarketTypeExclusionSelectorSchema,
} from '../../../../../../schema';
import { getData, postData } from '../../../../../../utils/api';
import {
  BonusMarketTypeExclusionSelector,
  BonusMarketTypeExclusionSelectorsResponse,
  deduplicateBonusMarketTypeExclusionSelectors,
  getBonusMarketTypeExclusionSelectorKey,
} from '../marketExclusions.utils';

const defaultValues: BonusMarketTypeExclusionSelectorFormData = {
  sportId: '',
  usedFor: '',
  type: '',
};

const getSortedKeys = (selectors: BonusMarketTypeExclusionSelector[]) =>
  selectors.map(getBonusMarketTypeExclusionSelectorKey).sort();

type SelectorContextValue = {
  state: {
    selectors: BonusMarketTypeExclusionSelector[];
  };
  actions: {
    addSelector: FormEventHandler<HTMLFormElement>;
    removeSelector: (key: string) => void;
    save: () => void;
    retry: () => void;
  };
  meta: {
    control: Control<BonusMarketTypeExclusionSelectorFormData>;
    sportOptions: Array<{ id: string; name: string }>;
    sportsById: Map<string, string>;
    isDuplicate: boolean;
    isDirty: boolean;
    isLoading: boolean;
    isError: boolean;
    isSaving: boolean;
    isReady: boolean;
  };
};

const SelectorContext = createContext<SelectorContextValue | null>(null);

export const SelectorProvider = ({ children }: PropsWithChildren) => {
  const queryClient = useQueryClient();

  const { enqueueSnackbar } = useToast();
  const { handleErrorMessage } = useToastErrorMessage();

  const { sportOptions, sportsById } = useMarketExclusionSports();

  // A null draft follows server state; an empty array intentionally removes every selector.
  const [draft, setDraft] = useState<BonusMarketTypeExclusionSelector[] | null>(null);

  // The controls build one candidate without changing the accumulated draft.
  const form = useForm<BonusMarketTypeExclusionSelectorFormData>({
    defaultValues,
    resolver: zodResolver(bonusMarketTypeExclusionSelectorSchema),
  });

  const [sportId, usedFor, type] = useWatch({ control: form.control, name: ['sportId', 'usedFor', 'type'] });

  const query = useQuery<BonusMarketTypeExclusionSelectorsResponse, ApiError>({
    queryKey: [QUERY_KEYS.bonusMarketTypeExclusionSelectors],
    queryFn: () => getData<BonusMarketTypeExclusionSelectorsResponse>(API_ROUTES.BONUS_MARKET_TYPE_EXCLUSION_SELECTORS),
  });

  useEffect(() => {
    if (query.error) handleErrorMessage(query.error);
  }, [handleErrorMessage, query.error]);

  const serverSelectors = useMemo(
    () => deduplicateBonusMarketTypeExclusionSelectors(query.data?.selectors || []),
    [query.data?.selectors]
  );

  const selectors = draft ?? serverSelectors;

  // Selector order does not affect whether the draft differs from the saved set.
  const selectorKeys = useMemo(() => getSortedKeys(selectors), [selectors]);
  const serverSelectorKeys = useMemo(() => getSortedKeys(serverSelectors), [serverSelectors]);

  const isDirty = JSON.stringify(selectorKeys) !== JSON.stringify(serverSelectorKeys);

  const currentSelector: BonusMarketTypeExclusionSelector = {
    ...(sportId && { sportId }),
    ...(usedFor && { usedFor }),
    ...(type && { type }),
  };

  const currentSelectorKey = getBonusMarketTypeExclusionSelectorKey(currentSelector);

  const isDuplicate = selectorKeys.includes(currentSelectorKey);

  const mutation = useMutation({
    mutationFn: (payload: BonusMarketTypeExclusionSelectorsResponse) =>
      postData<void>(API_ROUTES.BONUS_MARKET_TYPE_EXCLUSION_SELECTORS, payload),
    onSuccess: async (_response, payload) => {
      queryClient.setQueryData<BonusMarketTypeExclusionSelectorsResponse>(
        [QUERY_KEYS.bonusMarketTypeExclusionSelectors],
        payload
      );

      // Resume server state after saving; awaiting the reload keeps editing locked until it settles.
      setDraft(null);
      await query.refetch();

      enqueueSnackbar({ message: 'Market exclusion selectors saved.', variant: 'success' });
    },
    onError: handleErrorMessage,
  });

  const addSelector = form.handleSubmit(() => {
    if (mutation.isPending || isDuplicate) return;

    setDraft([...selectors, currentSelector]);
    form.reset(defaultValues);
  });

  const removeSelector = (key: string) => {
    if (mutation.isPending) return;

    setDraft(selectors.filter((selector) => getBonusMarketTypeExclusionSelectorKey(selector) !== key));
  };

  const save = () => {
    const payload = { selectors };

    mutation.mutate(payload);
  };

  const value: SelectorContextValue = {
    state: { selectors },
    actions: {
      addSelector,
      removeSelector,
      save,
      retry: () => void query.refetch(),
    },
    meta: {
      control: form.control,
      sportOptions,
      sportsById,
      isDuplicate,
      isDirty,
      isLoading: query.isLoading,
      isError: query.isError,
      isSaving: mutation.isPending,
      isReady: query.isSuccess,
    },
  };

  return <SelectorContext.Provider value={value}>{children}</SelectorContext.Provider>;
};

export const useSelectors = () => {
  const context = useContext(SelectorContext);

  if (!context) throw new Error('useSelectors must be used within its provider');

  return context;
};
